// Desktop notifications for THIS browser: a compact header icon (Session AQS, item 22), moved out
// of Settings, mirroring ThemeToggle's own icon-button pattern (the office header's established
// idiom for a per-browser preference).
//
// WHY THIS EXISTS AT ALL, and it is not a nicety. Session AFF built the office half of Web Push
// and put its only opt-in control on the office Messages page. Session AKF then retired that page
// from ELE's nav (Director decision, 18 August 2026) by removing its import from App.tsx: the
// component file is still on disk but nothing routes to it, so from that date NO OFFICE USER HAD
// ANY WAY TO SUBSCRIBE. The consequence was silent -- sendPushToOfficeAsync kept being called on
// every timesheet submission and every engineer message, found no office subscriptions, and
// resolved cleanly to nothing. Session ANZ moved the control to Settings; this session moved it
// again, to the header, so it is reachable from every office page rather than one settings screen
// (the office UI restructure audit found it buried).
//
// NOT ADMIN-ONLY. An office user is exactly who needs to know a person is trapped in a lift, and
// push is granted per browser per person, not per tenant.
//
// NO CONFIRM ON TURNING IT OFF, deliberately. The platform two-step delete rule governs entity
// deletes and consequential outward actions; this is a per-browser preference with a one-click
// path straight back. It IS the user-facing delete for a PushSubscription row (it calls
// POST /api/push/unsubscribe, which removes it server-side as well as in the browser), which is
// what RULE 1 asks of the model.
//
// A HEADER ICON HAS NO ROOM FOR A PARAGRAPH, so the explanation Settings gave in prose is now the
// tooltip (title), which also carries the specific refusal reason when enabling fails (denied /
// dismissed / other) -- the same three sentences Settings showed inline, just read on hover
// instead of always visible. The click target is the SAME as Settings offered: press to turn on
// when off, press to turn off when on; nothing here is a new interaction, only a smaller home for
// the existing one.
import { useEffect, useState } from "react";
import { Bell, BellOff } from "lucide-react";
import { api } from "../lib/api";
import { isPushSupported, getExistingSubscription, subscribeToPush, unsubscribePush, reconcilePushPermission } from "../lib/pushClient";

export function PushNotificationsButton() {
  const [supported] = useState(() => isPushSupported());
  const [publicKey, setPublicKey] = useState<string | null>(null);
  const [configured, setConfigured] = useState<boolean | null>(null);
  const [subscribed, setSubscribed] = useState<boolean | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!isPushSupported()) return;
      let status;
      try {
        status = await api.push.status();
      } catch {
        if (!cancelled) setConfigured(false);
        return;
      }
      if (cancelled) return;
      setConfigured(status.configured);
      setPublicKey(status.publicKey ?? null);
      // Session ANZ: if notifications were turned off in the browser since this subscription was
      // made, drop it rather than reporting "on" for a browser that will show nothing.
      const reconciled = await reconcilePushPermission();
      if (cancelled) return;
      const existing = reconciled === "revoked_cleared" ? null : await getExistingSubscription();
      if (!cancelled) setSubscribed(Boolean(existing));
    })().catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);

  async function enable(): Promise<void> {
    if (busy || !publicKey) return;
    setBusy(true);
    setError(null);
    const result = await subscribeToPush(publicKey);
    setBusy(false);
    if (result.ok) {
      setSubscribed(true);
      return;
    }
    setError(
      result.reason === "denied"
        ? "Your browser blocked notifications for this site. Allow them in the browser's site settings, then try again."
        : result.reason === "dismissed"
          ? "The notification prompt was closed without an answer. Click again and choose Allow."
          : "Could not turn notifications on. Try again, or check that this page is served over https.",
    );
  }

  async function disable(): Promise<void> {
    if (busy) return;
    setBusy(true);
    setError(null);
    await unsubscribePush();
    setBusy(false);
    setSubscribed(false);
  }

  // Nothing to offer: unsupported browser, or the server has no VAPID key configured. Renders
  // nothing (Settings said so in a sentence; a header icon has no room, and there is no action to
  // take here anyway).
  if (!supported || configured === false) return null;
  // Still resolving the initial status/reconcile read: render nothing rather than a flashing icon.
  if (configured !== true || subscribed === null) return null;

  const label = error
    ? error
    : subscribed
      ? "Desktop notifications are on for this browser. Click to turn off."
      : "Turn on desktop notifications for entrapments, SLA breaches and timesheet submissions.";
  const Icon = subscribed ? Bell : BellOff;

  return (
    <button
      type="button"
      onClick={() => void (subscribed ? disable() : enable())}
      disabled={busy}
      aria-label={label}
      title={label}
      aria-pressed={subscribed}
      className={`flex h-11 w-11 flex-none items-center justify-center rounded-ele outline-none transition focus-visible:ring-2 focus-visible:ring-ele-purple disabled:opacity-40 ${
        error ? "text-ele-danger" : subscribed ? "text-ele-green" : "text-ele-muted hover:text-ele-strong"
      }`}
    >
      <Icon aria-hidden size={20} />
    </button>
  );
}
